/**
 * Keeps the buy buttons in sync with Cornershop-only stock as the variant changes.
 *
 * Liquid can only read store_availabilities for a product's selected variant, so the
 * initial state is rendered server side and every subsequent variant is resolved by
 * fetching the 'cornershop-availability' section from the variant endpoint — the same
 * mechanism the theme's pickup-availability element uses.
 *
 * Required translation strings:
 * - cornershopOnly
 */

if (!customElements.get('cornershop-guard')) {
  class CornershopGuard extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.js-product');
      if (!this.section) return;

      this.requestIndex = 0;
      this.blocked = false;
      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.section.addEventListener('on:variant:change', this.variantChangeHandler);

      // The variant picker resets the buy button from a timeout of its own, and it
      // initialises after this element, so the block has to survive being overwritten
      // rather than simply being applied once.
      this.observer = new MutationObserver(this.enforce.bind(this));

      if (this.dataset.blocked === 'true') this.setBlocked(true);
    }

    disconnectedCallback() {
      if (this.section) this.section.removeEventListener('on:variant:change', this.variantChangeHandler);
      if (this.observer) this.observer.disconnect();
    }

    get addBtn() {
      return this.closest('form')?.querySelector('[name="add"]');
    }

    get notice() {
      return this.closest('form')?.querySelector('.js-cornershop-notice');
    }

    get dynamicCheckout() {
      return this.closest('form')?.querySelector('.product-info__dynamic-checkout-button');
    }

    /**
     * Handles 'on:variant:change' events on the product.
     * @param {object} evt - Event object.
     */
    async handleVariantChange(evt) {
      const btn = this.addBtn;
      if (!btn) return;

      this.observer.disconnect();

      // The variant picker resets the button before dispatching, so this is the
      // theme's own state for the newly selected variant, and what to restore.
      this.themeState = { disabled: btn.disabled, text: btn.textContent };

      const { variant } = evt.detail;
      if (!variant || !variant.available) {
        this.setBlocked(false);
        return;
      }

      // Block while checking, otherwise the button is briefly addable for a variant
      // that may turn out to be Cornershop-only.
      btn.disabled = true;

      const requestIndex = this.requestIndex + 1;
      this.requestIndex = requestIndex;

      const blocked = await this.isCornershopOnly(variant.id);
      if (requestIndex !== this.requestIndex) return;

      this.setBlocked(blocked);
    }

    /**
     * Fetches whether a variant is only available at the Cornershop.
     * @param {number} variantId - Variant id to check.
     * @returns {Promise<boolean>}
     */
    async isCornershopOnly(variantId) {
      let { rootUrl } = this.dataset;
      if (!rootUrl.endsWith('/')) rootUrl = `${rootUrl}/`;

      try {
        const response = await fetch(`${rootUrl}variants/${variantId}/?section_id=cornershop-availability`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const result = tmpl.content.querySelector('.js-cornershop-availability');
        return result?.dataset.cornershopOnly === 'true';
      } catch (error) {
        // Fail open: a failed request must not make the whole catalogue unbuyable.
        return false;
      }
    }

    /**
     * Applies or removes the Cornershop block.
     * @param {boolean} blocked - Is the variant only available at the Cornershop?
     */
    setBlocked(blocked) {
      const btn = this.addBtn;
      if (!btn) return;

      this.blocked = blocked;

      if (blocked) {
        this.enforce();
        this.observer.observe(btn, { attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true });
      } else {
        this.observer.disconnect();

        if (this.themeState) {
          btn.disabled = this.themeState.disabled;
          btn.textContent = this.themeState.text;
        }
      }

      const { notice } = this;
      if (notice) notice.hidden = !blocked;

      const { dynamicCheckout } = this;
      if (dynamicCheckout) dynamicCheckout.hidden = blocked;
    }

    /**
     * Re-applies the block to the buy button after anything else has changed it.
     */
    enforce() {
      const btn = this.addBtn;
      if (!btn || !this.blocked) return;

      // Only write when something actually differs, so the observer this runs from
      // does not retrigger itself.
      if (!btn.disabled) btn.disabled = true;
      if (btn.textContent !== theme.strings.cornershopOnly) btn.textContent = theme.strings.cornershopOnly;
    }
  }

  customElements.define('cornershop-guard', CornershopGuard);
}
